import React, { useState, useEffect, useRef, useMemo } from "react";
import JoditEditor from "jodit-react";
import { useSearchParams } from "react-router-dom";
import { Form, Spinner, Modal, Button } from "react-bootstrap";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Box,
  TablePagination,
} from "@mui/material";
import { FaEnvelope, FaCopy, FaCheck, FaDownload } from "react-icons/fa";
import { Table } from "react-bootstrap";
import authService from "../../../utils/authService";
import { getAdminCompanyListCached } from "../../../utils/adminCompanyCache";
import PlacementStudentsTable from "../../../components/PlacementStudentsTable";
import { downloadAllStudentResumes } from "../../../utils/downloadStudentResumes";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import type { ResumeStudentSource } from "../../../utils/mapStudentToResumeData";

function escapeHtmlForClipboard(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

interface Company {
  company_id: string;
  company_name: string;
  del_row?: boolean;
}

interface JobProfile {
  id: number;
  title: string;
  company_id: number | string;
  del_row?: boolean;
}

interface Course {
  course_id: string;
  course_name: string;
}

interface Batch {
  batch_id: string;
  batch_name: string;
  del_row?: boolean;
}

interface Branch {
  branch_id: string;
  branch: string;
}

interface VideoItem {
  type: string;
  video_id: string;
  status: string;
  otp?: string;
  playback_info?: string;
}

interface SecondarySkill {
  skill_id: string;
  skill_name: string;
  level: string;
  description?: string;
  cert_url?: string;
  type?: string;
}

interface EducationDetail {
  degree?: string;
  institute?: string;
  branch?: string;
  cgpa?: number | string;
  yop?: number | string;
}

interface ProjectDetail {
  type: string;
  description: string;
  link?: string;
}

interface CertificateDetail {
  name: string;
  cert_url: string;
}

interface Student {
  student_id: string;
  student_name: string;
  college: string;
  branch: string;
  batch_id: string;
  batch: string;
  course_id: string;
  course: string;
  cgpa?: number;
  score: string;
  score_percentage?: number;
  total_score: string;
  category: string;
  grade: string;
  lsa: number;
  leetcode?: string | null;
  hackerrank?: string | null;
  linkedin?: string | null;
  github?: string | null;
  profile_pic_url?: string;
  selected?: boolean;
  shortlisted?: boolean;
  hobbies?: string;
  videos?: VideoItem[];
  secondary_skills?: SecondarySkill[];
  primary_skills?: string[];
  student_project_details?: ProjectDetail[];
  student_primary_certificate_details?: CertificateDetail[];
  student_secondary_certificate_details?: CertificateDetail[];
}

interface SelectedStudent {
  student_id: string;
  student_name: string;
  college: string;
  branch: string;
  batch: string;
  batch_id: string;
  course: string;
  course_id: string;
  shortlisted?: boolean;
  selected?: boolean;
  email?: string;
  phone?: string;
  cgpa?: number;
  score?: string;
  score_percentage?: number;
  total_score?: string;
  category?: string;
  grade?: string;
  lsa?: number;
  leetcode?: string | null;
  hackerrank?: string | null;
  linkedin?: string | null;
  github?: string;
  profile_pic_url?: string;
  hobbies?: string;
  resume_objective?: string;
  subject_scores?: Record<string, number | string | null | undefined>;
  videos?: VideoItem[];
  secondary_skills?: SecondarySkill[];
  primary_skills?: string[];
  student_education_details?: EducationDetail[];
  student_project_details?: ProjectDetail[];
  student_primary_certificate_details?: CertificateDetail[];
  student_secondary_certificate_details?: CertificateDetail[];
}

/** Split full HTML email so the WYSIWYG editor only edits body inner HTML; envelope is restored on save. */
function splitHtmlDocument(html: string): { before: string; body: string; after: string } {
  const match = html.match(/([\s\S]*<body[^>]*>)([\s\S]*)(<\/body>[\s\S]*)/i);
  if (!match) {
    return { before: "", body: html, after: "" };
  }
  return { before: match[1], body: match[2], after: match[3] };
}

function joinHtmlDocument(before: string, body: string, after: string): string {
  if (!before && !after) return body;
  return before + body + after;
}

/** Category column: first two characters only, preserving original casing */
function formatCategoryColumnAbbrev(category: string | null | undefined): string {
  if (category == null || String(category).trim() === "") return "-";
  const s = String(category).trim();
  if (s.length <= 2) return s;
  return s.slice(0, 2);
}

const FilterSelect = ({
  label,
  value,
  onChange,
  options,
  valueKey,
  labelKey,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (e: SelectChangeEvent<string>) => void;
  options: any[];
  valueKey: string;
  labelKey: string;
  disabled?: boolean;
}) => (
  <FormControl variant="outlined" size="small" disabled={disabled} sx={{ minWidth: 140 }}>
    <InputLabel>{label}</InputLabel>
    <Select value={value} onChange={onChange} label={label} sx={{ height: 34 }}>
      <MenuItem value="">All</MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt[valueKey]} value={String(opt[valueKey])}>
          {opt[labelKey]}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const SelectStudents: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Filter data
  const [companies, setCompanies] = useState<Company[]>([]);
  const [jobProfiles, setJobProfiles] = useState<JobProfile[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [collegeName, setCollegeName] = useState<string>("");
  const [collegeId, setCollegeId] = useState<string>("");
  const [branches, setBranches] = useState<Branch[]>([]);

  // Filter values
  const [filterCompany, setFilterCompany] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterCourse, setFilterCourse] = useState("");
  const [filterBatch, setFilterBatch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");

  // Students
  const [students, setStudents] = useState<Student[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentsPage, setStudentsPage] = useState(0); // UI page is 0-based
  const [studentsPageSize, setStudentsPageSize] = useState(10);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const [hasSearchedStudents, setHasSearchedStudents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [downloadingResumes, setDownloadingResumes] = useState(false);
  const [downloadResumesProgress, setDownloadResumesProgress] = useState("");
  const [downloadResumesError, setDownloadResumesError] = useState("");

  // Right panel — fetched from API, stable across filter changes
  const [selectedStudents, setSelectedStudents] = useState<SelectedStudent[]>([]);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [lastSavedIds, setLastSavedIds] = useState<string[]>([]); // baseline for isDirty

  // Second section (Select students flow)
  const [showSecondSection, setShowSecondSection] = useState(false);

  // View mode: selected | sent | shortlist (selected API returns sent students; sent = same data)
  const [viewMode, setViewMode] = useState<"selected" | "sent" | "shortlist">("selected");

  // Left panel search
  const [tableSearch, setTableSearch] = useState("");

  // Modals for Projects and Certificates
  const [projectsModalStudent, setProjectsModalStudent] = useState<Student | SelectedStudent | null>(null);
  const [certificatesModalStudent, setCertificatesModalStudent] = useState<Student | SelectedStudent | null>(null);
  const [projectsModalTab, setProjectsModalTab] = useState<string>("0");
  const [certificatesModalTab, setCertificatesModalTab] = useState<string>("primary-0");
  const [certificatesModalMainTab, setCertificatesModalMainTab] = useState<"primary" | "secondary">("primary");

  const [videoModalStudent, setVideoModalStudent] = useState<Student | SelectedStudent | null>(null);
  const [videoModalVideos, setVideoModalVideos] = useState<VideoItem[]>([]);
  const [videoModalTab, setVideoModalTab] = useState<string>("0");
  const [videoModalPlayback, setVideoModalPlayback] = useState<{ otp: string; playback_info: string } | null>(null);
  const [videoModalLoading, setVideoModalLoading] = useState(false);
  const [videoModalError, setVideoModalError] = useState<string | null>(null);

  const [profilePicModal, setProfilePicModal] = useState<{ url: string; name: string } | null>(null);

  const [skillsModalStudent, setSkillsModalStudent] = useState<Student | SelectedStudent | null>(null);
  const [skillsModalTab, setSkillsModalTab] = useState<string>("0");

  useEffect(() => {
    fetchCompanies();
    fetchCourses();
  }, []);

  useEffect(() => {
    if (filterCompany) {
      fetchJobProfiles(filterCompany);
    } else {
      setJobProfiles([]);
      setFilterRole("");
    }
  }, [filterCompany]);

  useEffect(() => {
    if (filterRole) {
      fetchSelectedStudents(filterRole);
    } else {
      setSelectedStudents([]);
      setLastSavedIds([]);
    }
  }, [filterRole]);

  useEffect(() => {
    if (filterCourse) {
      fetchBatches(filterCourse);
    } else {
      setBatches([]);
      setFilterBatch("");
      setCollegeId("");
      setCollegeName("");
      setBranches([]);
      setFilterBranch("");
    }
  }, [filterCourse]);

  useEffect(() => {
    if (filterBatch) {
      fetchCollegeBranch(filterBatch);
    } else {
      setCollegeId("");
      setCollegeName("");
      setBranches([]);
      setFilterBranch("");
    }
  }, [filterBatch]);

  useEffect(() => {
    const companyFromUrl = searchParams.get("company");
    const jobProfileFromUrl = searchParams.get("job_profile");
    const viewFromUrl = searchParams.get("view");
    if (companyFromUrl && companies.length > 0) {
      const exists = companies.some((c) => String(c.company_id) === companyFromUrl);
      if (exists) setFilterCompany(companyFromUrl);
    }
    if (jobProfileFromUrl && filterCompany && jobProfiles.length > 0) {
      const exists = jobProfiles.some((j) => String(j.id) === jobProfileFromUrl);
      if (exists) setFilterRole(jobProfileFromUrl);
    }
    if (viewFromUrl === "sent" || viewFromUrl === "shortlist") {
      setViewMode(viewFromUrl);
      if (viewFromUrl === "shortlist") setShowSecondSection(false);
    }
  }, [searchParams, companies, jobProfiles, filterCompany]);

  const handleCompanyChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setFilterCompany(value);
    setFilterRole("");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("company", value);
        else next.delete("company");
        next.delete("job_profile");
        return next;
      },
      { replace: true }
    );
  };

  const handleJobProfileChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setFilterRole(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (filterCompany) next.set("company", filterCompany);
        if (value) next.set("job_profile", value);
        else next.delete("job_profile");
        return next;
      },
      { replace: true }
    );
  };

  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingJobProfiles, setLoadingJobProfiles] = useState(false);

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const { companies } = await getAdminCompanyListCached();
      setCompanies(((companies || []) as Company[]).filter((c) => !c.del_row));
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchJobProfiles = async (companyId: string) => {
    try {
      setLoadingJobProfiles(true);
      const response = await authService.get(`placement-officer/job-profiles/company/${companyId}`);
      setJobProfiles((response.data.job_profiles || []).filter((j: JobProfile) => !j.del_row));
    } catch (error) {
      console.error("Error fetching job profiles:", error);
    } finally {
      setLoadingJobProfiles(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await authService.get("admin/batch/get_all_courses_for_batch/");
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const fetchBatches = async (courseId: string) => {
    try {
      const response = await authService.get(`admin/batch/get_all_batches/${courseId}/`);
      setBatches((response.data.batches || []).filter((b: Batch) => !b.del_row));
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  };

  const fetchCollegeBranch = async (batchId: string) => {
    try {
      const response = await authService.post("placement-officer/job-profiles/college-branch", {
        batch_id: batchId,
      });
      setCollegeId(response.data.college_id || "");
      setCollegeName(response.data.college_name || "");
      setBranches(response.data.branches || []);
      setFilterBranch("");
    } catch (error) {
      console.error("Error fetching college/branch:", error);
      setCollegeId("");
      setCollegeName("");
      setBranches([]);
    }
  };

  const fetchSelectedStudents = async (jobId: string) => {
    try {
      setLoadingSelected(true);
      const response = await authService.get(
        `placement-officer/students/selected?job_id=${jobId}`
      );
      const list = response.data.students || [];
      setSelectedStudents(list);
      setLastSavedIds(list.map((s: SelectedStudent) => s.student_id).sort());
      setLoadingSelected(false);
    } catch (error) {
      setLoadingSelected(false);
      console.error("Error fetching selected students:", error);
    }
  };

  const handleSearch = async (pageOverride?: number, pageSizeOverride?: number) => {
    if (!filterCourse || !filterBatch) return;
    try {
      setLoadingStudents(true);
      const apiPage = pageOverride ?? studentsPage + 1;
      const apiPageSize = pageSizeOverride ?? studentsPageSize;
      const params: Record<string, string> = {
        course_id: filterCourse,
        batch_id: filterBatch,
        page: String(apiPage),
        page_size: String(apiPageSize),
      };
      if (filterCompany) params.company_id = filterCompany;
      if (filterRole) params.job_profile_id = filterRole;
      if (collegeId) params.college_id = collegeId;
      if (filterBranch) params.branch_id = filterBranch;

      const query = new URLSearchParams(params).toString();
      const response = await authService.get(
        `placement-officer/students/filter${query ? `?${query}` : ""}`
      );
      const payload = response.data ?? {};
      const fetched: Student[] = payload.students || [];
      setStudents(fetched);
      setStudentsTotal(typeof payload.total === "number" ? payload.total : fetched.length);
      setLoadingStudents(false);
    } catch (error) {
      setLoadingStudents(false);
      console.error("Error fetching students:", error);
    }
  };

  useEffect(() => {
    if (!showSecondSection || !hasSearchedStudents || !canSearch) return;
    handleSearch(studentsPage + 1, studentsPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentsPage, studentsPageSize]);

  useEffect(() => {
    setHasSearchedStudents(false);
    setStudents([]);
    setStudentsTotal(0);
    setStudentsPage(0);
  }, [filterCourse, filterBatch, filterBranch, collegeId, filterCompany, filterRole]);

  const isSelected = (student_id: string) =>
    selectedStudents.some((s) => s.student_id === student_id);

  const isShortlisted = (student_id: string) =>
    students.find((s) => s.student_id === student_id)?.shortlisted ?? false;

  const handleToggle = (student: Student | SelectedStudent) => {
    if (student.shortlisted) return;
    if (isSelected(student.student_id)) {
      setSelectedStudents((prev) => prev.filter((s) => s.student_id !== student.student_id));
    } else {
      // Resolve batch name: prefer student's own value, fallback to active filter
      const resolvedBatchId = student.batch_id || filterBatch;
      const resolvedBatch =
        student.batch ||
        batches.find((b) => b.batch_id === filterBatch)?.batch_name ||
        "";

      // Resolve course name: prefer student's own value, fallback to active filter
      const resolvedCourseId = student.course_id || filterCourse;
      const resolvedCourse =
        student.course ||
        courses.find((c) => c.course_id === filterCourse)?.course_name ||
        "";

      setSelectedStudents((prev) => [
        ...prev,
        {
          student_id: student.student_id,
          student_name: student.student_name,
          college: student.college || collegeName,
          branch: student.branch,
          batch: resolvedBatch,
          batch_id: resolvedBatchId,
          course: resolvedCourse,
          course_id: resolvedCourseId,
          shortlisted: student.shortlisted ?? false,
        },
      ]);
    }
  };

  const handleRemove = (student: Student | SelectedStudent) => {
    if (isShortlisted(student.student_id)) return;
    setSelectedStudents((prev) => prev.filter((s) => s.student_id !== student.student_id));
  };

  const handleSaveSelection = async () => {
    if (!filterRole) {
      setSaveError("Please select a Job Profile before saving.");
      return;
    }
    try {
      setSaveError("");
      setSaving(true);
      await authService.put("placement-officer/job-profiles/candidates/sync", {
        jp_id: Number(filterRole),
        student_ids: selectedStudents.map((s) => s.student_id),
      });
      setSaving(false);
      const ids = selectedStudents.map((s) => s.student_id).sort();
      setLastSavedIds(ids);
      fetchSelectedStudents(filterRole);
    } catch (err: any) {
      setSaving(false);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save selection. Please try again.";
      setSaveError(msg);
    }
  };

  const handleDownloadAllResumes = async () => {
    if (!selectedStudents.length) {
      setDownloadResumesError("No sent students to download.");
      return;
    }
    try {
      setDownloadResumesError("");
      setDownloadingResumes(true);
      setDownloadResumesProgress(`Preparing 0/${selectedStudents.length}...`);

      const jobTitle =
        jobProfiles.find((j) => String(j.id) === filterRole)?.title ||
        "sent-students";
      const safeJob = Array.from(jobTitle)
        .map((ch) => {
          const code = ch.charCodeAt(0);
          if (code < 32 || '<>:"/\\|?*'.includes(ch)) return "_";
          return ch === " " ? "_" : ch;
        })
        .join("")
        .slice(0, 60);

      await downloadAllStudentResumes(selectedStudents as ResumeStudentSource[], {
        zipFileName: `${safeJob}-resumes.zip`,
        onProgress: (done, total) => {
          setDownloadResumesProgress(`Generating ${done}/${total}...`);
        },
      });
      setDownloadResumesProgress("");
    } catch (err: any) {
      console.error("Failed to download resumes:", err);
      setDownloadResumesError(err?.message || "Failed to download resumes. Please try again.");
      setDownloadResumesProgress("");
    } finally {
      setDownloadingResumes(false);
    }
  };

  const [showMailOptionsModal, setShowMailOptionsModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [sendingMailInProgress, setSendingMailInProgress] = useState(false);
  const [gettingTemplateInProgress, setGettingTemplateInProgress] = useState(false);
  const [gettingPlacementEmailTemplate, setGettingPlacementEmailTemplate] = useState(false);
  const [sendingPlacementEmailTemplate, setSendingPlacementEmailTemplate] = useState(false);
  const [placementEmailEditorOpen, setPlacementEmailEditorOpen] = useState(false);
  const [placementEmailEditDraft, setPlacementEmailEditDraft] = useState("");
  /** From GET placement-email-template — keys substituted as {key} in HTML */
  const [placementEmailPlaceholders, setPlacementEmailPlaceholders] = useState<string[]>([]);
  /** DOCTYPE + outer tags + `<body…>` … `</body>` tail — kept when saving so API still receives full document */
  const [placementEmailDocWrap, setPlacementEmailDocWrap] = useState<{ before: string; after: string }>({
    before: "",
    after: "",
  });
  const [placementJoditMountKey, setPlacementJoditMountKey] = useState(0);

  const placementEmailJoditConfig = useMemo(
    () => ({
      height: 440,
      toolbarSticky: false,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
    }),
    []
  );
  const [mailError, setMailError] = useState("");
  const [mailSuccess, setMailSuccess] = useState("");
  const [credentialsTemplate, setCredentialsTemplate] = useState<string>("");
  const [credentialsContacts, setCredentialsContacts] = useState<{ name: string; email: string; password: string }[]>([]);
  const [copiedContactIndex, setCopiedContactIndex] = useState<number | null>(null);
  const [copiedContactsTable, setCopiedContactsTable] = useState(false);
  const STUDENT_FILTERS_KEY = "po_students_filters";

  useEffect(() => {
    const saved = sessionStorage.getItem(STUDENT_FILTERS_KEY);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved) as { course?: string; batch?: string };
      if (parsed.course) setFilterCourse(parsed.course);
      if (parsed.batch) setFilterBatch(parsed.batch);
    } catch (e) {
      console.error("Error restoring student filters:", e);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      STUDENT_FILTERS_KEY,
      JSON.stringify({
        course: filterCourse,
        batch: filterBatch,
      })
    );
  }, [filterCourse, filterBatch]);

  const handleSendMailToContacts = async () => {
    if (!filterRole) return;
    try {
      setMailError("");
      setMailSuccess("");
      setSendingMailInProgress(true);
      const res = await authService.post("placement-officer/job-profiles/candidates/notify-contacts", {
        jp_id: Number(filterRole),
      });
      setSendingMailInProgress(false);
      const msg = res?.data?.message || "Notification sent successfully.";
      const count = res?.data?.emails_sent ?? 0;
      setMailSuccess(`${msg} (${count} email(s) sent)`);
    } catch (err: any) {
      setSendingMailInProgress(false);
      setMailError(
        err?.response?.data?.detail || err?.response?.data?.message || err?.message || "Failed to send mail."
      );
    }
  };

  const handleGetCredentialsTemplate = async () => {
    if (!filterRole) return;
    try {
      setMailError("");
      setMailSuccess("");
      setCredentialsTemplate("");
      setCredentialsContacts([]);
      setCopiedContactIndex(null);
      setCopiedContactsTable(false);
      setGettingTemplateInProgress(true);
      const res = await authService.post("placement-officer/job-profiles/candidates/notify-credentials", {
        jp_id: Number(filterRole),
      });
      setGettingTemplateInProgress(false);
      const msg = res?.data?.message || "Contact information retrieved.";
      setMailSuccess(msg);
      setCredentialsTemplate(res?.data?.template || "");
      setCredentialsContacts(res?.data?.contacts || []);
    } catch (err: any) {
      setGettingTemplateInProgress(false);
      setMailError(
        err?.response?.data?.detail || err?.response?.data?.message || err?.message || "Failed to get credentials template."
      );
    }
  };

  const handleOpenPlacementEmailEditor = async () => {
    if (!filterRole) return;
    try {
      setMailError("");
      setMailSuccess("");
      setGettingPlacementEmailTemplate(true);
      const res = await authService.get("placement-officer/job-profiles/placement-email-template", {
        params: { jp_id: Number(filterRole) },
      });
      setGettingPlacementEmailTemplate(false);
      const data = res?.data as { template?: string; placeholders?: string[] } | undefined;
      const tpl =
        typeof data?.template === "string"
          ? data.template
          : typeof data === "string"
            ? data
            : "";
      const rawPh = data?.placeholders;
      const ph = Array.isArray(rawPh)
        ? rawPh.filter((p): p is string => typeof p === "string")
        : [];
      setPlacementEmailPlaceholders(ph);
      const parts = splitHtmlDocument(tpl);
      setPlacementEmailDocWrap({ before: parts.before, after: parts.after });
      setPlacementEmailEditDraft(parts.body);
      setPlacementJoditMountKey((k) => k + 1);
      setPlacementEmailEditorOpen(true);
    } catch (err: any) {
      setGettingPlacementEmailTemplate(false);
      setMailError(
        err?.response?.data?.detail || err?.response?.data?.message || err?.message || "Failed to load email template."
      );
    }
  };

  const handleSavePlacementEmailTemplate = async () => {
    if (!filterRole) return;
    try {
      setMailError("");
      setMailSuccess("");
      setSendingPlacementEmailTemplate(true);
      const res = await authService.post("placement-officer/job-profiles/placement-email-template/send", {
        jp_id: Number(filterRole),
        template: joinHtmlDocument(placementEmailDocWrap.before, placementEmailEditDraft, placementEmailDocWrap.after),
      });
      setSendingPlacementEmailTemplate(false);
      const msg = res?.data?.message || "Notification sent successfully.";
      const count = res?.data?.emails_sent ?? 0;
      setMailSuccess(`${msg} (${count} email(s) sent)`);
      setPlacementEmailEditorOpen(false);
      setPlacementEmailEditDraft("");
      setPlacementEmailPlaceholders([]);
      setPlacementEmailDocWrap({ before: "", after: "" });
    } catch (err: any) {
      setSendingPlacementEmailTemplate(false);
      setMailError(
        err?.response?.data?.detail || err?.response?.data?.message || err?.message || "Failed to send email."
      );
    }
  };

  const handleBackFromPlacementEmailEditor = () => {
    setPlacementEmailEditorOpen(false);
    setPlacementEmailEditDraft("");
    setPlacementEmailPlaceholders([]);
    setPlacementEmailDocWrap({ before: "", after: "" });
    setMailError("");
    setMailSuccess("");
  };

  const handleCopyContact = (c: { name: string; email: string; password: string }, index: number) => {
    const text = `Name: ${c.name}\nUser ID: ${c.email}\nPassword: ${c.password}`;
    navigator.clipboard.writeText(text).then(() => {
      setMailSuccess("Contact details copied.");
      setCopiedContactIndex(index);
      setTimeout(() => setCopiedContactIndex(null), 2000);
    }).catch(() => setMailError("Failed to copy."));
  };

  const handleCopyContactsTable = async () => {
    if (credentialsContacts.length === 0) return;
    const rowsHtml = credentialsContacts
      .map(
        (c) =>
          `<tr><td style="border:1px solid #dee2e6;padding:8px">${escapeHtmlForClipboard(c.name)}</td><td style="border:1px solid #dee2e6;padding:8px">${escapeHtmlForClipboard(c.email)}</td><td style="border:1px solid #dee2e6;padding:8px">${escapeHtmlForClipboard(c.password)}</td></tr>`
      )
      .join("");
    const html = `<table border="1" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #dee2e6;font-family:system-ui,-apple-system,sans-serif;font-size:13px">
<thead><tr style="background:#f8f9fa"><th style="border:1px solid #dee2e6;padding:8px;text-align:left">Name</th><th style="border:1px solid #dee2e6;padding:8px;text-align:left">User ID</th><th style="border:1px solid #dee2e6;padding:8px;text-align:left">Password</th></tr></thead>
<tbody>${rowsHtml}</tbody>
</table>`;
    const plain = [
      "Name\tUser ID\tPassword",
      ...credentialsContacts.map((c) => `${c.name}\t${c.email}\t${c.password}`),
    ].join("\n");
    setMailError("");
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      setMailSuccess("Table copied with formatting. Paste into your email (Ctrl+V).");
      setCopiedContactsTable(true);
      setTimeout(() => setCopiedContactsTable(false), 2000);
    } catch {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
          }),
        ]);
        setMailSuccess("Table copied (HTML). Paste into your email.");
        setCopiedContactsTable(true);
        setTimeout(() => setCopiedContactsTable(false), 2000);
      } catch {
        try {
          await navigator.clipboard.writeText(plain);
          setMailSuccess("Table copied as plain text (tabs between columns).");
          setCopiedContactsTable(true);
          setTimeout(() => setCopiedContactsTable(false), 2000);
        } catch {
          setMailError("Failed to copy table.");
        }
      }
    }
  };

  const handleCopyTemplate = async () => {
    if (!credentialsTemplate) return;
    try {
      // Use execCommand as primary: inserts HTML so paste into email renders formatted
      const div = document.createElement("div");
      div.innerHTML = credentialsTemplate;
      div.contentEditable = "true";
      div.style.position = "fixed";
      div.style.left = "-9999px";
      div.style.top = "0";
      document.body.appendChild(div);
      const range = document.createRange();
      range.selectNodeContents(div);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      const ok = document.execCommand("copy");
      document.body.removeChild(div);
      sel?.removeAllRanges();
      if (ok) {
        setMailSuccess("Template copied. Paste into your email editor (Ctrl+V) — it will appear formatted.");
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        throw new Error("execCommand failed");
      }
    } catch {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([credentialsTemplate], { type: "text/html" }),
          }),
        ]);
        setMailSuccess("Template copied. Paste into your email — it will appear formatted.");
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch {
        await navigator.clipboard.writeText(credentialsTemplate);
        setMailSuccess("Template copied as HTML. Paste into email HTML/source view if needed.");
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    }
  };

  const handleOpenMailModal = () => {
    setMailError("");
    setMailSuccess("");
    setCredentialsTemplate("");
    setCredentialsContacts([]);
    setCopySuccess(false);
    setCopiedContactIndex(null);
    setCopiedContactsTable(false);
    setPlacementEmailEditorOpen(false);
    setPlacementEmailEditDraft("");
    setPlacementEmailPlaceholders([]);
    setPlacementEmailDocWrap({ before: "", after: "" });
    setShowMailOptionsModal(true);
  };

  const handleCloseMailModal = () => {
    setShowMailOptionsModal(false);
    setMailError("");
    setMailSuccess("");
    setCredentialsTemplate("");
    setCopySuccess(false);
    setCopiedContactsTable(false);
    setPlacementEmailEditorOpen(false);
    setPlacementEmailEditDraft("");
    setPlacementEmailPlaceholders([]);
    setPlacementEmailDocWrap({ before: "", after: "" });
  };

  const currentIds = selectedStudents.map((s) => s.student_id).sort();
  const isDirty =
    currentIds.length !== lastSavedIds.length ||
    currentIds.some((id, i) => id !== lastSavedIds[i]);

  const filteredStudents = students.filter((s) => {
    const q = tableSearch.toLowerCase();
    return (
      s.student_id.toLowerCase().includes(q) ||
      s.student_name.toLowerCase().includes(q) ||
      s.college.toLowerCase().includes(q) ||
      s.branch.toLowerCase().includes(q) ||
      (s.category ?? "").toLowerCase().includes(q) ||
      (s.grade ?? "").toLowerCase().includes(q)
    );
  });

  const canSearch = filterCourse && filterBatch;
  const shortlistedCount = selectedStudents.filter((s) =>
    !!(s.shortlisted ?? isShortlisted(s.student_id))
  ).length;

  const shortlistedStudents = selectedStudents.filter((s) =>
    !!(s.shortlisted ?? isShortlisted(s.student_id))
  );

  const tableData =
    viewMode === "shortlist"
      ? shortlistedStudents
      : selectedStudents;

  const tableLoading =
    loadingCompanies || loadingJobProfiles || loadingSelected;

  const displayStudents = showSecondSection ? filteredStudents : tableData;
  const displayLoading = showSecondSection ? loadingStudents : tableLoading;
  const displayEmptyMessage = showSecondSection
    ? (!hasSearchedStudents
      ? "Select Course and Batch, then click Search"
      : students.length === 0
      ? "No students found for the selected filters"
      : "No students match the search")
    : (!filterRole
      ? "Select a Job Profile to view students"
      : viewMode === "sent"
      ? "No sent students for this job profile"
      : viewMode === "shortlist"
      ? "No shortlisted students"
      : "No students selected for this job profile");

  const getProjectTabLabels = (projects: ProjectDetail[] = []) => {
    const typeCounts: Record<string, number> = {};
    projects.forEach((p) => {
      const t = p.type || "Project";
      typeCounts[t] = (typeCounts[t] ?? 0) + 1;
    });
    const typeIndex: Record<string, number> = {};
    return projects.map((p, i) => {
      const type = p.type || "Project";
      const total = typeCounts[type] ?? 1;
      const idx = (typeIndex[type] = (typeIndex[type] ?? 0) + 1);
      return { key: String(i), label: total > 1 ? `${type}${idx}` : type };
    });
  };

  const openProjectsModal = (e: React.MouseEvent, student: Student | SelectedStudent) => {
    e.stopPropagation();
    setProjectsModalStudent(student);
    setProjectsModalTab("0");
  };

  const openVideoModal = (e: React.MouseEvent, student: Student | SelectedStudent) => {
    e.stopPropagation();
    const videos = student.videos ?? [];
    if (videos.length === 0) return;
    setVideoModalStudent(student);
    setVideoModalVideos(videos);
    setVideoModalTab("0");
    setVideoModalError(null);
    const first = videos[0];
    if (first?.otp && first?.playback_info) {
      setVideoModalPlayback({ otp: first.otp, playback_info: first.playback_info });
      setVideoModalLoading(false);
    } else if (first?.video_id) {
      setVideoModalPlayback(null);
      setVideoModalLoading(true);
      authService.get(`student/videos/${first.video_id}/`).then((res) => {
        const otp = res.data?.otp ?? "";
        const playback = res.data?.playback_info ?? "";
        if (otp && playback) setVideoModalPlayback({ otp, playback_info: playback });
        else setVideoModalError("Video not available");
      }).catch(() => setVideoModalError("Failed to load video")).finally(() => setVideoModalLoading(false));
    } else {
      setVideoModalPlayback(null);
      setVideoModalError("Video not available");
    }
  };

  const getVideoTabLabels = (videos: VideoItem[] = []) => {
    const typeCounts: Record<string, number> = {};
    videos.forEach((v) => {
      const t = v.type || "Video";
      typeCounts[t] = (typeCounts[t] ?? 0) + 1;
    });
    const typeIndex: Record<string, number> = {};
    return videos.map((v, i) => {
      const type = v.type || "Video";
      const total = typeCounts[type] ?? 1;
      const idx = (typeIndex[type] = (typeIndex[type] ?? 0) + 1);
      return { key: String(i), label: total > 1 ? `${type}${idx}` : type };
    });
  };

  const renderVideoModalContent = () => {
    if (!videoModalStudent || videoModalVideos.length === 0) {
      return (
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <p className="text-muted mb-0">No videos.</p>
        </div>
      );
    }
    const tabs = getVideoTabLabels(videoModalVideos);
    const currentVideo = videoModalVideos[Number(videoModalTab)];
    const videoUrl = videoModalPlayback
      ? `https://player.vdocipher.com/v2/?otp=${videoModalPlayback.otp}&playbackInfo=${videoModalPlayback.playback_info}`
      : null;
    const singleVideo = videoModalVideos.length === 1;
    return (
      <>
        {!singleVideo && (
          <div className="flex-shrink-0 px-3 pt-3 pb-2 border-bottom bg-white">
            <div className="d-flex flex-row flex-nowrap gap-1">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`btn btn-sm flex-shrink-0 ${videoModalTab === t.key ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => {
                    setVideoModalTab(t.key);
                    const vid = videoModalVideos[Number(t.key)];
                    if (vid?.otp && vid?.playback_info) {
                      setVideoModalPlayback({ otp: vid.otp, playback_info: vid.playback_info });
                    } else if (vid?.video_id) {
                      fetchVideoForTab(vid.video_id);
                    } else {
                      setVideoModalPlayback(null);
                      setVideoModalError("Video not available");
                    }
                  }}
                >
                  {t.label.charAt(0).toUpperCase() + t.label.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          {videoModalLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" size="sm" />
            </div>
          ) : videoModalError ? (
            <p className="text-danger mb-0">{videoModalError}</p>
          ) : videoUrl && currentVideo ? (
            <div>
              <iframe
                src={videoUrl}
                width="100%"
                height="400"
                title="Video"
                allowFullScreen
                allow="encrypted-media; autoplay; clipboard-write"
                className="border-0"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
              />
            </div>
          ) : null}
        </div>
      </>
    );
  };

  const fetchVideoForTab = async (videoId: string) => {
    setVideoModalPlayback(null);
    setVideoModalError(null);
    setVideoModalLoading(true);
    try {
      const res = await authService.get(`student/videos/${videoId}/`);
      const otp = res.data?.otp ?? "";
      const playback = res.data?.playback_info ?? "";
      if (otp && playback) {
        setVideoModalPlayback({ otp, playback_info: playback });
      } else {
        setVideoModalError("Video not available");
      }
    } catch {
      setVideoModalError("Failed to load video");
    } finally {
      setVideoModalLoading(false);
    }
  };

  /** Max height for modal body so tabs stay fixed and detail scrolls (viewport minus header/chrome). */
  const modalBodyScrollStyle: React.CSSProperties = {
    maxHeight: "min(75vh, calc(100dvh - 9rem))",
    minHeight: 0,
  };

  const renderSkillsModalContent = () => {
    if (!skillsModalStudent) return null;
    const skills = skillsModalStudent.secondary_skills ?? [];
    if (skills.length === 0) {
      return (
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <p className="text-muted mb-0">No skills.</p>
        </div>
      );
    }
    const singleSkill = skills.length === 1;
    const current = skills[Number(skillsModalTab)];
    if (!current) {
      return (
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <p className="text-muted mb-0">No skill selected.</p>
        </div>
      );
    }
    return (
      <>
        {!singleSkill && (
          <div className="flex-shrink-0 px-3 pt-3 pb-2 border-bottom bg-white">
            <div className="d-flex flex-row flex-nowrap gap-1">
              {skills.map((s, i) => (
                <button
                  key={s.skill_id}
                  type="button"
                  className={`btn btn-sm flex-shrink-0 ${skillsModalTab === String(i) ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setSkillsModalTab(String(i))}
                >
                  {s.skill_name || `Skill ${i + 1}`}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <div className="p-2 border rounded bg-light">
            <div className="mt-2"><strong>Level:</strong> {current.level || "-"}</div>
            {current.description != null && current.description !== "" && (
              <div className="mt-2"><strong>Description:</strong> {current.description}</div>
            )}
            {current.cert_url && (
              <div className="mt-2">
                <strong>Certificate:</strong>
                <div className="mt-1">
                  <Box
                    component="img"
                    src={resolveMediaUrl(current.cert_url)}
                    alt={`${current.skill_name} certificate`}
                    sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const openSkillsModal = (e: React.MouseEvent, student: Student | SelectedStudent) => {
    e.stopPropagation();
    setSkillsModalStudent(student);
    setSkillsModalTab("0");
  };

  const openCertificatesModal = (e: React.MouseEvent, student: Student | SelectedStudent) => {
    e.stopPropagation();
    setCertificatesModalStudent(student);
    const prim = student.student_primary_certificate_details?.length ?? 0;
    const sec = student.student_secondary_certificate_details?.length ?? 0;
    if (prim > 0) {
      setCertificatesModalMainTab("primary");
      setCertificatesModalTab("primary-0");
    } else if (sec > 0) {
      setCertificatesModalMainTab("secondary");
      setCertificatesModalTab("secondary-0");
    } else {
      setCertificatesModalMainTab("primary");
      setCertificatesModalTab("primary-0");
    }
  };

  const projectsCount = (s: Student | SelectedStudent) => (s.student_project_details?.length ?? 0);
  const videosCount = (s: Student | SelectedStudent) => (s.videos?.length ?? 0);
  const skillsCount = (s: Student | SelectedStudent) => (s.secondary_skills?.length ?? 0);
  const certificatesCount = (s: Student | SelectedStudent) =>
    (s.student_primary_certificate_details?.length ?? 0) +
    (s.student_secondary_certificate_details?.length ?? 0);

  const renderProjectsModalContent = () => {
    if (!projectsModalStudent) return null;
    const projects = projectsModalStudent.student_project_details ?? [];
    const tabs = getProjectTabLabels(projects);
    if (projects.length === 0) {
      return (
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <p className="text-muted mb-0">No projects.</p>
        </div>
      );
    }
    return (
      <>
        <div className="flex-shrink-0 px-3 pt-3 pb-2 border-bottom bg-white">
          <div className="d-flex flex-row flex-nowrap gap-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`btn btn-sm flex-shrink-0 ${projectsModalTab === t.key ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setProjectsModalTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <div className="p-2 border rounded bg-light">
            {projects[Number(projectsModalTab)] && (
              <div>
                <div><strong>Type:</strong> {projects[Number(projectsModalTab)].type || "-"}</div>
                <div className="mt-2">
                  <strong>Description:</strong>
                  {projects[Number(projectsModalTab)].description ? (
                    <div className="mt-1" dangerouslySetInnerHTML={{ __html: projects[Number(projectsModalTab)].description }} />
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderCertificatesModalContent = () => {
    if (!certificatesModalStudent) return null;
    const prim = certificatesModalStudent.student_primary_certificate_details ?? [];
    const sec = certificatesModalStudent.student_secondary_certificate_details ?? [];
    return (
      <>
        <div className="flex-shrink-0 px-3 pt-3 pb-2 border-bottom bg-white">
          <div className="d-flex flex-row flex-nowrap align-items-center gap-1">
            <div className="d-flex flex-row flex-nowrap gap-1 flex-shrink-0">
              <button
                type="button"
                className={`btn btn-sm ${certificatesModalMainTab === "primary" ? "btn-primary" : "btn-outline-secondary"}`}
                disabled={prim.length === 0}
                onClick={() => { setCertificatesModalMainTab("primary"); setCertificatesModalTab("primary-0"); }}
              >
                Primary ({prim.length})
              </button>
              <button
                type="button"
                className={`btn btn-sm ${certificatesModalMainTab === "secondary" ? "btn-primary" : "btn-outline-secondary"}`}
                disabled={sec.length === 0}
                onClick={() => { setCertificatesModalMainTab("secondary"); setCertificatesModalTab("secondary-0"); }}
              >
                Secondary ({sec.length})
              </button>
            </div>
            {certificatesModalMainTab === "primary" && prim.length > 1 && (
              <div className="d-flex flex-row flex-nowrap gap-1 ms-auto flex-shrink-0">
                {prim.map((_, i) => (
                  <button
                    key={`primary-${i}`}
                    type="button"
                    className={`btn btn-sm btn-outline-primary ${certificatesModalTab === `primary-${i}` ? "active" : ""}`}
                    onClick={() => setCertificatesModalTab(`primary-${i}`)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
            {certificatesModalMainTab === "secondary" && sec.length > 1 && (
              <div className="d-flex flex-row flex-nowrap gap-1 ms-auto flex-shrink-0">
                {sec.map((_, i) => (
                  <button
                    key={`secondary-${i}`}
                    type="button"
                    className={`btn btn-sm btn-outline-primary ${certificatesModalTab === `secondary-${i}` ? "active" : ""}`}
                    onClick={() => setCertificatesModalTab(`secondary-${i}`)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
        {certificatesModalMainTab === "primary" && prim.length > 0 && (
          <>
            <div className="p-2 border rounded bg-light">
              {prim.length > 1
                ? prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)] && (
                  <div>
                    <div><strong>Name:</strong> {prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)].name}</div>
                    {prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)].cert_url && (
                      <Box
                        component="img"
                        src={resolveMediaUrl(prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)].cert_url)}
                        alt={prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)].name}
                        sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }}
                      />
                    )}
                  </div>
                )
                : prim[0] && (
                  <div>
                    <div><strong>Name:</strong> {prim[0].name}</div>
                    {prim[0].cert_url && (
                      <Box component="img" src={resolveMediaUrl(prim[0].cert_url)} alt={prim[0].name} sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }} />
                    )}
                  </div>
                )}
            </div>
          </>
        )}
        {certificatesModalMainTab === "secondary" && sec.length > 0 && (
          <>
            <div className="p-2 border rounded bg-light">
              {sec.length > 1
                ? sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)] && (
                  <div>
                    <div><strong>Name:</strong> {sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)].name}</div>
                    {sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)].cert_url && (
                      <Box
                        component="img"
                        src={resolveMediaUrl(sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)].cert_url)}
                        alt={sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)].name}
                        sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }}
                      />
                    )}
                  </div>
                )
                : sec[0] && (
                  <div>
                    <div><strong>Name:</strong> {sec[0].name}</div>
                    {sec[0].cert_url && (
                      <Box component="img" src={resolveMediaUrl(sec[0].cert_url)} alt={sec[0].name} sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }} />
                    )}
                  </div>
                )}
            </div>
          </>
        )}
        {certificatesModalMainTab === "primary" && prim.length === 0 && <p className="text-muted mb-0">No primary certificates.</p>}
        {certificatesModalMainTab === "secondary" && sec.length === 0 && <p className="text-muted mb-0">No secondary certificates.</p>}
        </div>
      </>
    );
  };

  return (
    <div className="d-flex flex-column h-100 min-h-0 flex-fill">
      {/* Section 1: Company, Job Profile, Save, Select Students */}
      <div className="d-flex flex-wrap align-items-center gap-3 p-3 flex-shrink-0 border-bottom border-secondary-subtle">
        <FilterSelect
          label="Company"
          value={filterCompany}
          onChange={handleCompanyChange}
          options={companies}
          valueKey="company_id"
          labelKey="company_name"
        />
        <FilterSelect
          label="Job Profile"
          value={filterRole}
          onChange={handleJobProfileChange}
          options={jobProfiles}
          valueKey="id"
          labelKey="title"
          disabled={!filterCompany}
        />
        <span className="ms-2 small fw-medium text-body-secondary">
          Sent ({selectedStudents.length}) Selected ({shortlistedCount})
          </span>
        <div className="ms-auto d-flex align-items-center gap-2">
          {saveError && viewMode !== "shortlist" && (
            <span className="text-danger small">{saveError}</span>
          )}
          {downloadResumesError && viewMode !== "shortlist" && (
            <span className="text-danger small">{downloadResumesError}</span>
          )}
          {downloadingResumes && downloadResumesProgress && (
            <span className="text-body-secondary small">{downloadResumesProgress}</span>
          )}
          {viewMode !== "shortlist" && (
            <>
              <button
                className="btn btn-sm btn-primary"
                disabled={!filterRole || !isDirty || saving}
                onClick={handleSaveSelection}
              >
                {saving ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Saving...</>
                ) : (
                  "Save Selection"
                )}
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={!filterRole || selectedStudents.length === 0 || downloadingResumes}
                onClick={handleDownloadAllResumes}
                title="Download all sent student resumes as a ZIP"
              >
                {downloadingResumes ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Downloading...</>
                ) : (
                  <><FaDownload className="me-1" />Resumes</>
                )}
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={!filterRole}
                onClick={handleOpenMailModal}
                title="Mail"
              >
                <FaEnvelope className="me-1" />
                Mail
              </button>
              <button
                className={`btn btn-sm ${showSecondSection ? "btn-primary" : "btn-outline-primary"}`}
                disabled={!filterRole}
                onClick={() => setShowSecondSection((prev) => !prev)}
              >
                Select Students
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters — shown when Select Students clicked */}
      {showSecondSection && (
        <div className="d-flex flex-wrap align-items-center gap-3 p-3 flex-shrink-0 border-top border-bottom border-secondary-subtle">
          <FilterSelect
            label="Course"
            value={filterCourse}
            onChange={(e) => setFilterCourse(e.target.value)}
            options={courses}
            valueKey="course_id"
            labelKey="course_name"
          />
          <FilterSelect
            label="Batch"
            value={filterBatch}
            onChange={(e) => setFilterBatch(e.target.value)}
            options={batches}
            valueKey="batch_id"
            labelKey="batch_name"
            disabled={!filterCourse}
          />
          <TextField
            size="small"
            label="College"
            value={collegeName || ""}
            disabled
            placeholder={filterBatch ? "N/A" : "Select Batch first"}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
            InputProps={{
              sx: {
                height: 34,
                "& input": { py: 0, px: 1.5, boxSizing: "border-box" },
              },
            }}
          />
          <FilterSelect
            label="Branch"
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
            options={branches}
            valueKey="branch_id"
            labelKey="branch"
            disabled={!filterBatch || branches.length === 0}
          />
          <div className="ms-auto">
            <button
              className="btn btn-sm btn-primary"
              disabled={!canSearch}
              onClick={() => {
                setHasSearchedStudents(true);
                setStudentsPage(0);
                setLoadingStudents(true);
                handleSearch(1, studentsPageSize);
              }}
            >
              Search
            </button>
          </div>
        </div>
      )}

      {/* Single table: same columns for Selected, Sent, Shortlist, and pool */}
      {showSecondSection && (
        <div className="mx-2 mt-2 px-2 py-2 bg-body-secondary bg-opacity-10">
          <Form.Control
            size="sm"
            placeholder="Search by Name, College, Branch, Category, Grade..."
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
          />
        </div>
      )}
      <PlacementStudentsTable
        students={displayStudents as any}
        loading={displayLoading}
        emptyMessage={displayEmptyMessage}
        selectionMode={showSecondSection ? "checkbox" : (viewMode === "shortlist" ? "none" : "remove")}
        selectColumnLabel={showSecondSection ? "Select" : "Remove"}
        isSelected={isSelected}
        onToggle={(student) => handleToggle(student as any)}
        onRemove={(student) => handleRemove(student as any)}
        rowClickable={showSecondSection}
        onRowClick={(student) => handleToggle(student as any)}
      />

      {showSecondSection && hasSearchedStudents && (studentsTotal > 0 || loadingStudents) && (
        <TablePagination
          component="div"
          count={studentsTotal}
          page={studentsPage}
          onPageChange={(_, newPage) => {
            setLoadingStudents(true);
            setStudentsPage(newPage);
          }}
          rowsPerPage={studentsPageSize}
          onRowsPerPageChange={(e) => {
            setLoadingStudents(true);
            setStudentsPageSize(parseInt(e.target.value, 10));
            setStudentsPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            flexShrink: 0,
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#fafafa",
            "& .MuiTablePagination-toolbar": { minHeight: 44, px: 2 },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "12px",
              color: "#666",
              mb: 0,
            },
            "& .MuiTablePagination-actions": { ml: 1 },
            "& .MuiIconButton-root": { p: "6px" },
            "& .Mui-disabled": { opacity: 0.45 },
          }}
        />
      )}

    <Modal show={showMailOptionsModal} onHide={handleCloseMailModal} centered size="xl" dialogClassName="mx-auto">
      <Modal.Header closeButton className="d-flex align-items-center">
        <Modal.Title className="mb-0 fs-6">Mail Options</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center overflow-auto min-h-0 px-3 py-3" style={modalBodyScrollStyle}>
        {(sendingMailInProgress ||
          gettingTemplateInProgress ||
          gettingPlacementEmailTemplate ||
          sendingPlacementEmailTemplate) ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" size="sm" />
          </div>
        ) : placementEmailEditorOpen ? (
          <div className="text-start">
            {mailError && <p className="text-danger small mb-2">{mailError}</p>}
            {mailSuccess && <p className="text-success small mb-2">{mailSuccess}</p>}
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                "& .jodit-container": { border: "none !important" },
              }}
            >
              <JoditEditor
                key={placementJoditMountKey}
                value={placementEmailEditDraft}
                config={placementEmailJoditConfig}
                onChange={(html) => setPlacementEmailEditDraft(html)}
              />
            </Box>
          </div>
        ) : credentialsContacts.length > 0 ? (
          <div className="text-start">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            {mailError && <p className="text-danger small mb-2">{mailError}</p>}
            {mailSuccess && <p className="text-success small mb-2">{mailSuccess}</p>}
              <button
                type="button"
                className={`btn btn-sm ${copiedContactsTable ? "btn-success" : "btn-outline-secondary"} d-inline-flex align-items-center gap-1`}
                onClick={() => void handleCopyContactsTable()}
                title="Copy the full table as HTML — paste into Outlook/Gmail to keep table layout"
              >
                {copiedContactsTable ? (
                  <>
                    <FaCheck size={14} /> Copied
                  </>
                ) : (
                  <>
                    <FaCopy size={14} /> Copy
                  </>
                )}
              </button>
            </div>
            <div className="table-responsive shadow-sm">
              <Table striped hover bordered size="sm" className="mb-0 align-middle w-100">
                <thead className="table-light small text-body-secondary">
                  <tr>
                    <th className="fw-semibold text-nowrap">Name</th>
                    <th className="fw-semibold text-nowrap">User ID</th>
                    <th className="fw-semibold text-nowrap">Password</th>
                    <th className="text-center fw-semibold text-nowrap" style={{ width: 72 }}>
                      Copy
                    </th>
                  </tr>
                </thead>
              <tbody>
                {credentialsContacts.map((c, i) => (
                  <tr key={i}>
                    <td>{c.name}</td>
                    <td>{c.email}</td>
                    <td>{c.password}</td>
                    <td className="text-center">
                      <button
                        type="button"
                        className={`btn btn-link btn-sm p-1 ${copiedContactIndex === i ? "text-success" : "text-secondary"}`}
                        title={copiedContactIndex === i ? "Copied!" : "Copy contact details"}
                        onClick={() => handleCopyContact(c, i)}
                      >
                        {copiedContactIndex === i ? <FaCheck size={16} /> : <FaCopy size={16} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </Table>
            </div>
          </div>
        ) : credentialsTemplate ? (
          <div>
            {mailError && <p className="text-danger small mb-2">{mailError}</p>}
            {mailSuccess && <p className="text-success small mb-2">{mailSuccess}</p>}
            <div className="d-flex justify-content-end mb-2">
              <button
                type="button"
                className={`btn btn-link p-1 ${copySuccess ? "text-success" : "text-secondary"}`}
                title={copySuccess ? "Copied!" : "Copy template"}
                onClick={handleCopyTemplate}
              >
                {copySuccess ? <FaCheck size={18} /> : <FaCopy size={18} />}
              </button>
            </div>
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
                overflow: "auto",
                maxHeight: 200,
              }}
              dangerouslySetInnerHTML={{ __html: credentialsTemplate }}
            />
          </div>
        ) : (mailError || mailSuccess) ? (
          <>
            {mailError && <p className="text-danger small mb-2">{mailError}</p>}
            {mailSuccess && <p className="text-success small mb-2">{mailSuccess}</p>}
          </>
        ) : (
          <div className="d-flex gap-2 justify-content-center flex-wrap">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleSendMailToContacts}
            >
              Send Mail with default template
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleGetCredentialsTemplate}
            >
              Get Contacts
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => void handleOpenPlacementEmailEditor()}
            >
              Edit template and send mail
            </Button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-center align-items-center flex-wrap gap-2">
        {placementEmailEditorOpen &&
          !sendingMailInProgress &&
          !gettingTemplateInProgress &&
          !gettingPlacementEmailTemplate &&
          !sendingPlacementEmailTemplate && (
            <>
              <Button variant="outline-secondary" size="sm" type="button" onClick={handleBackFromPlacementEmailEditor}>
                Back
              </Button>
              <Button variant="primary" size="sm" type="button" onClick={() => void handleSavePlacementEmailTemplate()}>
                Save
              </Button>
            </>
          )}
        <Button variant="secondary" size="sm" onClick={handleCloseMailModal}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
    </div>
  );
};

export default SelectStudents;
